// Top-of-page navigation. Single "Menu" dropdown on the left frees up clear
// space around the centred logo. Language + sign controls on the far right.
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { List, CaretDown } from "@phosphor-icons/react";
import { BrandLockup } from "./BrandLockup";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { PathToggle } from "./PathToggle";
import { useAuth } from "../lib/AuthContext";

const ADMIN_EMAILS = ["lea@millen.com", "glenn@millen.com"];

export function SiteNav() {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const isAdmin = user && typeof user === "object" && ADMIN_EMAILS.includes((user.email || "").toLowerCase());
    const [menuOpen, setMenuOpen] = useState(false);

    // Close the menu on any route or hash change.
    useEffect(() => { setMenuOpen(false); }, [location.pathname, location.hash]);

    return (
        <header
            className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#FDFBF7]/85 border-b border-[#E5E1D8]/50"
            data-testid="site-nav"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-72 grid grid-cols-[1fr_auto_1fr] items-center gap-6 relative">
                {/* LEFT — single Menu trigger. */}
                <div className="justify-self-start">
                    <MenuDropdown
                        open={menuOpen}
                        setOpen={setMenuOpen}
                        user={user}
                        isAdmin={isAdmin}
                        onLogout={async () => { setMenuOpen(false); navigate("/"); await logout(); }}
                    />
                </div>

                {/* CENTER — logo, perfectly centred regardless of left/right widths. */}
                <Link
                    to="/"
                    className="justify-self-center group transition-transform hover:scale-[1.03]"
                    data-testid="nav-logo"
                >
                    <BrandLockup size="2xl" tagline />
                </Link>

                {/* RIGHT — language + sign controls. */}
                <div className="flex items-center gap-3 sm:gap-4 justify-self-end text-sm text-[#5C5A59]">
                    <PathToggle />
                    <LanguageSwitcher />
                    {user && typeof user === "object" ? (
                        <span className="hidden lg:inline text-sm text-[#8A8786]" data-testid="nav-user-email">
                            {user.email}
                        </span>
                    ) : (
                        <>
                            <Link
                                to="/sign-in"
                                className="hidden sm:inline hover:text-[#6B7DB8] transition-colors whitespace-nowrap"
                                data-testid="nav-signin"
                            >
                                {t("nav.sign_in")}
                            </Link>
                            <Link
                                to="/sign-up"
                                className="px-4 py-2 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white text-sm font-medium transition-all hover:-translate-y-0.5 shadow-rem-soft whitespace-nowrap"
                                data-testid="nav-signup"
                            >
                                {t("nav.sign_up")}
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}

function MenuDropdown({ open, setOpen, user, isAdmin, onLogout }) {
    const { t } = useTranslation();
    const ref = useRef(null);

    // Close on click outside or Escape.
    useEffect(() => {
        if (!open) return;
        function onDown(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        function onKey(e) { if (e.key === "Escape") setOpen(false); }
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, setOpen]);

    const items = [
        { to: "/#story",    label: t("nav.story") },
        { to: "/#demos",    label: t("nav.demos", "Demos") },
        { to: "/#how",      label: t("nav.how_it_works") },
        { to: "/#pricing",  label: t("nav.pricing") },
        { to: "/research",  label: t("nav.research") },
        { to: "/tutorial",  label: t("nav.tutorial") },
        { to: "/partners",  label: t("footer.platform_partners", "Partners & distributors") },
    ];

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-haspopup="menu"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/70 hover:bg-white border border-[#E5E1D8] hover:border-[#8C9ECB] text-sm text-[#2D2B2A] hover:text-[#4A5A95] transition-all shadow-rem-soft"
                data-testid="nav-menu-trigger"
            >
                <List size={18} weight="regular" />
                <span className="font-medium">{t("nav.menu", "Menu")}</span>
                <CaretDown size={12} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -4, scale: 0.98 }}
                        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                        role="menu"
                        className="absolute left-0 top-full mt-3 w-64 rounded-2xl bg-white border border-[#E5E1D8] shadow-rem-card overflow-hidden"
                        data-testid="nav-menu-panel"
                    >
                        <ul className="py-2">
                            {items.map((it) => (
                                <li key={it.to}>
                                    <Link
                                        to={it.to}
                                        onClick={() => setOpen(false)}
                                        className="block px-5 py-2.5 text-sm text-[#2D2B2A] hover:bg-[#F4F6FB] hover:text-[#4A5A95] transition-colors"
                                        data-testid={`nav-menu-item-${it.to.replace(/[/#]/g, "")}`}
                                        role="menuitem"
                                    >
                                        {it.label}
                                    </Link>
                                </li>
                            ))}
                            {user && typeof user === "object" && (
                                <>
                                    <li className="border-t border-[#E5E1D8] mt-2 pt-2">
                                        <Link
                                            to="/dashboard"
                                            onClick={() => setOpen(false)}
                                            className="block px-5 py-2.5 text-sm text-[#2D2B2A] hover:bg-[#F4F6FB] hover:text-[#4A5A95] transition-colors"
                                            data-testid="nav-menu-dashboard"
                                            role="menuitem"
                                        >
                                            {t("nav.dashboard")}
                                        </Link>
                                    </li>
                                    {isAdmin && (
                                        <li>
                                            <Link
                                                to="/admin"
                                                onClick={() => setOpen(false)}
                                                className="block px-5 py-2.5 text-sm text-[#8A6A0E] hover:bg-[#FBF6E6] transition-colors font-medium"
                                                data-testid="nav-menu-admin"
                                                role="menuitem"
                                            >
                                                {t("nav.admin")}
                                            </Link>
                                        </li>
                                    )}
                                    <li>
                                        <button
                                            onClick={onLogout}
                                            className="block w-full text-left px-5 py-2.5 text-sm text-[#5C5A59] hover:bg-[#F4F6FB] hover:text-[#4A5A95] transition-colors"
                                            data-testid="nav-menu-logout"
                                            role="menuitem"
                                        >
                                            {t("nav.sign_out")}
                                        </button>
                                    </li>
                                </>
                            )}
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
